import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import type { LiveModel } from '../adapter'
import { formatDuration } from '../format'
import { viewersText } from '../PlatformBlock'
import { t } from '../../i18n'
import { needsYou } from './model'
import { Button, Chip, Icon, Tag } from '../../ui'
import { useSession } from '../../session'
import { BILLING_PATH, barLabel } from '../../billing'

/** Keep ?theme when switching between Studio and Status. */
function useThemed(path: string) {
  const loc = useLocation()
  const theme = new URLSearchParams(loc.search).get('theme')
  return theme ? `${path}?theme=${theme}` : path
}

/**
 * The bar of the Live screens — the system's broadcast strip (BroadcastIndicator, AUDIT CB-01/13):
 * Studio | Status as tabs (selection = accent with its edge; Status counts what needs you), the main
 * ON AIR chip with the timer (the sender decides: decision 3; it pulses only when the header logo
 * does not, motion.tsx), outputs on air and viewers in mono.
 * The broadcast title and the bell wait for the API (stage 2): not drawn.
 * Phone: 8 px gaps, so tabs, the chip with its 16 px timer and the outputs fit one row at 390 px.
 * The plan label (BILL-B5a, design §10.1): the trial's days, a plan that ended, a failed payment,
 * new broadcasts blocked — a link to Plan & usage; nothing for a paid plan in good standing or the box.
 * `end`: the strip's one action, last in the row (ScreenStudioOnAir: "… · End broadcast"; CB-01/CB-04).
 */
export function StudioBar({ m, now, tab, dropped, phone, chats, onChats, end }: {
  m: LiveModel; now: number; tab: 'studio' | 'status'; dropped: boolean; phone: boolean;
  /** Studio only: the right column shows chats; the button switches it */
  chats?: boolean; onChats?: () => void;
  /** Studio, wider than a phone: End broadcast while live */
  end?: ReactNode;
}) {
  const studio = useThemed('/')
  const status = useThemed('/status')
  const billing = useThemed(BILLING_PATH)
  const { user } = useSession()
  const plan = barLabel(user, now)
  const s = m.summary
  const attention = m.dests.filter(needsYou).length
  const enabled = m.dests.filter((d) => d.enabled).length
  const dur = s.live && s.since ? formatDuration(Math.max(0, Math.floor((now - Date.parse(s.since)) / 1000))) : ''
  const heading = s.live ? t('state.live') : dropped ? t('state.dropped') : t('state.ready')
  // tabs as in the system's top bar: selection = accent tint + 2 px accent edge below (02 «selection is shape-coded», CB-08)
  const tabCls = (on: boolean) =>
    `flex min-h-8 items-center gap-1.5 rounded-md ${phone ? 'px-2.5 max-md:min-h-[40px]' : 'px-3'} text-[13px] no-underline ${on ? 'bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'text-muted hover:text-text'}`
  return (
    <div data-testid="studio-bar" className={`flex flex-none flex-wrap items-center ${phone ? 'gap-x-2' : 'gap-x-3'} gap-y-2 border-b border-line px-3 py-2 md:px-5`}>
      <nav aria-label={t('bar.views')} data-testid="studio-tabs" className="flex gap-0.5">
        <Link to={studio} data-testid="tab-studio" aria-current={tab === 'studio' ? 'page' : undefined} className={tabCls(tab === 'studio')}>{t('bar.studio')}</Link>
        <Link to={status} data-testid="tab-status" aria-current={tab === 'status' ? 'page' : undefined} className={tabCls(tab === 'status')}>
          {t('bar.status')}
          {attention > 0 && (
            <span data-testid="tab-status-attention" className="inline-flex items-center gap-1 font-mono text-[11px] text-warn" aria-label={t('bar.needYou', { n: attention })}>
              <Icon name="state-warn" size={12} />{attention}
            </span>
          )}
        </Link>
      </nav>
      {tab === 'studio' && (
        <h1 data-testid="input-state" className={`m-0 min-w-0 truncate text-ui-title font-medium ${phone ? 'sr-only' : ''}`}>{heading}</h1>
      )}
      <div className={`ml-auto flex items-center ${phone ? 'gap-2' : 'gap-2.5'}`}>
        {/* DS: PL-01 — the plan label is a state Chip (access to going live), not the monochrome sx-plan */}
        {plan && (
          <>
            <Link to={billing} data-testid="plan-label" data-tone={plan.tone} title={t('plan.bar.hint')} className="flex no-underline">
              <Chip state={plan.tone} size="sm">{plan.text}</Chip>
            </Link>
            <span className="h-5 w-px bg-line-2" aria-hidden />
          </>
        )}
        <span data-testid="onair-pill" data-state={s.live ? 'live' : dropped ? 'dropped' : 'off'} className={`flex items-center ${phone ? 'gap-2' : 'gap-3'} whitespace-nowrap`}>
          {/* ingest lost = fail (StateSystem); the main ON AIR chip of the view */}
          <Chip state={s.live ? 'live' : dropped ? 'fail' : 'idle'} size="lg" main={s.live}>
            {s.live ? t('bar.live') : dropped ? t('bar.obsStopped') : t('bar.offAir')}
          </Chip>
          {s.live && <span className="tabular font-mono text-base leading-5 font-medium text-text">{dur}</span>}
        </span>
        <span className="h-5 w-px bg-line-2" aria-hidden />
        <span data-testid="bar-outputs" className="tabular whitespace-nowrap font-mono text-xs text-text-soft">
          {t('bar.out', { on: m.onAir, n: enabled })}{s.viewers ? ` · ${viewersText(s.viewers.total)}` : ''}
        </span>
        {/* VERT-1 (VT-04): both inputs' bitrates while the vertical one is on air — "6.0 Mbps in · 9:16 4.5 Mbps" */}
        {s.live && m.vertical?.live && (
          <span data-testid="bar-in" className="tabular flex items-center gap-1.5 whitespace-nowrap font-mono text-xs text-text-soft">
            {t('vert.sum.inN', { v: s.bitrateMbps !== undefined ? `${s.bitrateMbps.toFixed(1)} Mbps` : '—' })}
            <Tag>{t('vert.tag')}</Tag>
            {m.vertical.bitrateMbps !== undefined ? `${m.vertical.bitrateMbps.toFixed(1)} Mbps` : '—'}
          </span>
        )}
        {onChats && !phone && (
          <Button size="sm" data-testid="bar-chat" aria-pressed={!!chats} onClick={onChats} title={t('bar.chatHint')}
            className={chats ? 'border-accent/45 bg-accent/10 text-accent' : 'text-text-soft'}>
            <Icon name="chat" size={16} />{t('bar.chat')}
          </Button>
        )}
        {end}
      </div>
    </div>
  )
}

/** The system's chat icon (12 px in dense places, 16 px elsewhere). */
export function ChatIcon({ size = 16 }: { size?: number }) {
  return <Icon name="chat" size={size <= 13 ? 12 : 16} />
}
